import * as Yup from 'yup';
import { useState, useContext } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { UserContext } from '../../Contexts/UserContext';

import { Link, Stack, TextField, IconButton, InputAdornment, Alert, Collapse } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import { login, getUserDataFromToken } from '../../api/auth';
import { toast } from 'react-toastify';

const LoginSchema = Yup.object().shape({
	email: Yup.string().email('Email must be a valid email address').required('Email is required'),
	password: Yup.string().required('Password is required')
});

// Styled Components
const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
	borderRadius: '8px'
}));

export default function LoginForm() {
	const navigate = useNavigate();
	const { setUser } = useContext(UserContext);

	const [showPassword, setShowPassword] = useState(false);
	const [showError, setShowError] = useState(false);

	const handleShowPassword = () => {
		setShowPassword((show) => !show);
	};

	const handleLoginFormSubmit = async (formData) => {
		try {
			const userLoginData = await login(formData);
			if (!userLoginData) {
				setShowError(true);
				return;
			}

			setShowError(false);
			console.log('Login data is : ', userLoginData);
			const userDataFromCookie = getUserDataFromToken();
			setUser(userDataFromCookie);
			if (userDataFromCookie?.isVerified) {
				return navigate('/dashboard', { replace: true });
			}
			//NOTE : We cant use userDataFromCookie here from cookie since cookie is not set if verified=false, hence userDataFromCookie is undefined here.
			navigate(`/verify-code?email=${userLoginData?.data?.email}&forgotPassword=false`, {
				replace: true
			});
		} catch (err) {
			toast.error('Error Logging In');
		}
	};

	const formik = useFormik({
		initialValues: {
			email: '',
			password: ''
		},
		validationSchema: LoginSchema,
		onSubmit: handleLoginFormSubmit
	});

	const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

	return (
		<>
			{showError && (
				<Stack spacing={3}>
					<Collapse in={showError}>
						<Alert
							variant="filled"
							severity="error"
							onClose={() => {
								setShowError(false);
							}}
							sx={{ mb: 2 }}
						>
							Incorrect Credentials
						</Alert>
					</Collapse>
				</Stack>
			)}

			<FormikProvider value={formik}>
				<Form autoComplete="off" noValidate onSubmit={handleSubmit}>
					<Stack spacing={3}>
						<TextField
							fullWidth
							autoComplete="username"
							type="email"
							label="Email address"
							{...getFieldProps('email')}
							error={Boolean(touched.email && errors.email)}
							helperText={touched.email && errors.email}
						/>

						<TextField
							fullWidth
							autoComplete="current-password"
							type={showPassword ? 'text' : 'password'}
							label="Password"
							{...getFieldProps('password')}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton onClick={handleShowPassword} edge="end">
											<Icon icon={showPassword ? eyeFill : eyeOffFill} />
										</IconButton>
									</InputAdornment>
								)
							}}
							error={Boolean(touched.password && errors.password)}
							helperText={touched.password && errors.password}
						/>
					</Stack>
					<Stack direction="row" alignItems="center" justifyContent="end" sx={{ my: 2 }}>
						<Link component={RouterLink} variant="subtitle2" to="/forgot-password">
							Forgot password?
						</Link>
					</Stack>
					<StyledLoadingButton
						fullWidth
						size="large"
						type="submit"
						variant="contained"
						loading={isSubmitting}
					>
						Login
					</StyledLoadingButton>

					<Stack direction="row" alignItems="center" justifyContent="center" sx={{ my: 2 }}>
						<Link variant="subtitle1" component={RouterLink} to="/register">
							Don't have an account? Sign Up
						</Link>
					</Stack>
				</Form>
			</FormikProvider>
		</>
	);
}
